import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getFacilities } from '../../lib/services/facilities'
import {
  EHR_VENDOR_LABELS,
  canTriggerIntegrationSync,
  connectEhr,
  getEhrConnections,
  getIntegrationStatus,
  isIntegrationReadOnly,
  runIntegrationSync,
  supportContextFromError,
  verifyEhrConnection,
  type EhrAuthMode,
  type EhrConnectionStatus,
  type EhrVendor,
} from '../../lib/services/integrations'

const EHR_STATUS_STYLES: Record<EhrConnectionStatus, string> = {
  connected: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  failed: 'text-red-700 bg-red-50 border-red-200',
  pending: 'text-amber-700 bg-amber-50 border-amber-200',
  revoked: 'text-slate-700 bg-slate-50 border-slate-200',
  verifying: 'text-blue-700 bg-blue-50 border-blue-200',
}

export function IntegrationsStatusPage() {
  const { viewerContext } = useAuth()
  const readOnly = isIntegrationReadOnly(viewerContext)
  const canSync = canTriggerIntegrationSync(viewerContext)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)
  const [selectedFacilityId, setSelectedFacilityId] = useState('')
  const [vendor, setVendor] = useState<EhrVendor>('modmed')
  const [authMode, setAuthMode] = useState<EhrAuthMode>('oauth2_client_credentials')
  const [fhirBaseUrl, setFhirBaseUrl] = useState('')
  const [oauthClientId, setOauthClientId] = useState('')
  const [oauthSecretRef, setOauthSecretRef] = useState('')
  const [apiKeyRef, setApiKeyRef] = useState('')
  const [scopesInput, setScopesInput] = useState('')
  const [baaConfirmed, setBaaConfirmed] = useState(false)
  const [ehrActionError, setEhrActionError] = useState<string | null>(null)
  const [ehrActionSuccess, setEhrActionSuccess] = useState<string | null>(null)

  const integrationStatusQuery = useQuery({
    queryKey: ['integrations-status'],
    queryFn: () => getIntegrationStatus(),
  })

  const facilitiesQuery = useQuery({
    queryKey: ['integrations-facilities'],
    queryFn: () => getFacilities(),
    enabled: canSync,
  })

  useEffect(() => {
    if (selectedFacilityId) return
    const firstViewerFacility = viewerContext?.facility_ids?.[0]
    const firstLoadedFacility = facilitiesQuery.data?.[0]?.facility_id
    const nextFacilityId = firstViewerFacility || firstLoadedFacility || ''
    if (nextFacilityId) {
      setSelectedFacilityId(nextFacilityId)
    }
  }, [facilitiesQuery.data, selectedFacilityId, viewerContext?.facility_ids])

  const ehrConnectionsQuery = useQuery({
    queryKey: ['integrations-ehr-connections', selectedFacilityId],
    queryFn: () => getEhrConnections(selectedFacilityId),
    enabled: canSync && selectedFacilityId.length > 0,
  })

  const connectMutation = useMutation({
    mutationFn: async () =>
      connectEhr({
        facility_id: selectedFacilityId,
        vendor,
        auth_mode: authMode,
        fhir_base_url: fhirBaseUrl.trim(),
        oauth_client_id: authMode === 'api_key' ? null : (oauthClientId.trim() || null),
        oauth_client_secret_ref: authMode === 'api_key' ? null : (oauthSecretRef.trim() || null),
        api_key_ref: authMode === 'api_key' ? (apiKeyRef.trim() || null) : null,
        scopes: scopesInput
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value.length > 0),
        baa_acknowledged_at: baaConfirmed ? new Date().toISOString() : null,
      }),
    onSuccess: async (connection) => {
      setEhrActionError(null)
      setEhrActionSuccess(`EHR connection saved for ${EHR_VENDOR_LABELS[connection.vendor]}.`)
      setFhirBaseUrl('')
      setOauthClientId('')
      setOauthSecretRef('')
      setApiKeyRef('')
      setScopesInput('')
      setBaaConfirmed(false)
      await ehrConnectionsQuery.refetch()
    },
    onError: (error) => {
      setEhrActionSuccess(null)
      setEhrActionError((error as Error).message)
    },
  })

  const verifyMutation = useMutation({
    mutationFn: (ehrConnectionId: string) => verifyEhrConnection(ehrConnectionId),
    onSuccess: async (result) => {
      setEhrActionError(null)
      setEhrActionSuccess(
        result.status === 'connected'
          ? 'EHR verification succeeded.'
          : `EHR verification returned ${result.status}.`,
      )
      await ehrConnectionsQuery.refetch()
    },
    onError: (error) => {
      setEhrActionSuccess(null)
      setEhrActionError((error as Error).message)
    },
  })

  const supportContext = integrationStatusQuery.error ? supportContextFromError(integrationStatusQuery.error) : null
  const selectedFacilityName =
    facilitiesQuery.data?.find((facility) => facility.facility_id === selectedFacilityId)?.facility_name ||
    selectedFacilityId
  const canSubmitConnection =
    !readOnly &&
    selectedFacilityId.length > 0 &&
    fhirBaseUrl.trim().length > 0 &&
    baaConfirmed &&
    (authMode === 'api_key' || oauthClientId.trim().length > 0)

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Integrations Status</h1>
        <p className="mt-1 text-sm text-slate-600">Read-only monitor for integration health, ingestion outputs, and bounded EHR connection management.</p>
      </header>

      <section className="v11-section p-5">
        {integrationStatusQuery.isLoading && <p className="text-sm text-slate-500">Loading integration status...</p>}
        {integrationStatusQuery.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <p className="text-sm font-semibold">Failed to load integration status</p>
            <p className="text-xs">{(integrationStatusQuery.error as Error).message}</p>
            <p className="mt-1 text-xs text-red-600">Support context: {supportContext}</p>
          </div>
        )}
        {integrationStatusQuery.data && (
          <div className="space-y-2">
            <p className="text-sm text-slate-700">
              Source: <span className="font-semibold">{integrationStatusQuery.data.source_system}</span>
            </p>
            <p className="text-sm text-slate-700">
              Status:{' '}
              <span
                className={
                  integrationStatusQuery.data.status === 'configured'
                    ? 'text-emerald-700 font-semibold'
                    : integrationStatusQuery.data.status === 'not_configured'
                      ? 'text-amber-700 font-semibold'
                      : 'text-red-700 font-semibold'
                }
              >
                {integrationStatusQuery.data.status}
              </span>
            </p>
            <p className="text-sm text-slate-700">
              Latest run:{' '}
              <span className="font-semibold">
                {integrationStatusQuery.data.latest_ingestion_timestamp || 'No successful run'}
              </span>
            </p>
          </div>
        )}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Actions</h2>
        <p className="mt-1 text-xs text-slate-500">Integration sync is available only to account admins or explicitly granted users.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {canSync && (
            <button
              className="btn-primary text-sm"
              disabled={isActing}
              onClick={async () => {
                setActionError(null)
                setActionSuccess(null)
                setIsActing(true)
                try {
                  await runIntegrationSync()
                  setActionSuccess('Integration sync started.')
                  await integrationStatusQuery.refetch()
                } catch (error) {
                  setActionError((error as Error).message)
                } finally {
                  setIsActing(false)
                }
              }}
            >
              Run Sync
            </button>
          )}
          <Link
            to="/integrations/runs"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            View Run History
          </Link>
        </div>
        {readOnly && <p className="mt-2 text-xs text-slate-500">Read-only mode enabled for your role.</p>}
      </section>

      <section className="v11-section p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">EHR Connection Manager</h2>
            <p className="mt-1 text-xs text-slate-500">
              Bounded Sprint 4 admin UI for the shipped EHR connect, verify, and status lifecycle.
            </p>
          </div>
          <div className="w-full md:w-72">
            <label htmlFor="ehr-facility" className="mb-1 block text-xs font-semibold text-slate-600">Facility</label>
            <select
              id="ehr-facility"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              disabled={facilitiesQuery.isLoading || (facilitiesQuery.data ?? []).length === 0}
              value={selectedFacilityId}
              onChange={(event) => setSelectedFacilityId(event.target.value)}
            >
              <option value="">Select facility</option>
              {(facilitiesQuery.data ?? []).map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>
                  {facility.facility_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {facilitiesQuery.error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <p className="text-sm font-semibold">Failed to load facilities</p>
            <p className="text-xs">{(facilitiesQuery.error as Error).message}</p>
          </div>
        )}

        <div className="mt-4 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-3">
            <div className="v11-dashboard-card p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Current connections</h3>
                  <p className="text-xs text-slate-500">
                    {selectedFacilityName ? `Facility: ${selectedFacilityName}` : 'Select a facility to view current EHR connections.'}
                  </p>
                </div>
                <button
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={!selectedFacilityId || ehrConnectionsQuery.isFetching}
                  onClick={() => {
                    setEhrActionError(null)
                    setEhrActionSuccess(null)
                    void ehrConnectionsQuery.refetch()
                  }}
                >
                  Refresh
                </button>
              </div>

              {ehrConnectionsQuery.isLoading && <p className="mt-3 text-sm text-slate-500">Loading EHR connections...</p>}
              {ehrConnectionsQuery.error && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                  <p className="text-sm font-semibold">Failed to load EHR connections</p>
                  <p className="text-xs">{(ehrConnectionsQuery.error as Error).message}</p>
                  <p className="mt-1 text-xs text-red-600">Support context: {supportContextFromError(ehrConnectionsQuery.error)}</p>
                </div>
              )}
              {!ehrConnectionsQuery.isLoading && !ehrConnectionsQuery.error && selectedFacilityId && (ehrConnectionsQuery.data ?? []).length === 0 && (
                <p className="mt-3 text-sm text-slate-500">No EHR connections configured for this facility.</p>
              )}

              <div className="mt-3 space-y-3">
                {(ehrConnectionsQuery.data ?? []).map((connection) => (
                  <div key={connection.ehr_connection_id} className="v11-dashboard-card p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{EHR_VENDOR_LABELS[connection.vendor]}</p>
                        <p className="text-xs text-slate-500">
                          Auth: {connection.auth_mode} • Connection ID: {connection.ehr_connection_id}
                        </p>
                        {connection.last_verified_at && (
                          <p className="mt-1 text-xs text-slate-500">
                            Last verified: {new Date(connection.last_verified_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${EHR_STATUS_STYLES[connection.connection_status]}`}>
                        {connection.connection_status}
                      </span>
                    </div>
                    {!connection.baa_acknowledged_at && (
                      <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        BAA acknowledgment is still required before verification can succeed.
                      </p>
                    )}
                    {connection.last_error && (
                      <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {connection.last_error}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        className="btn-primary text-sm"
                        disabled={verifyMutation.isPending || !connection.baa_acknowledged_at}
                        onClick={() => {
                          setEhrActionError(null)
                          setEhrActionSuccess(null)
                          verifyMutation.mutate(connection.ehr_connection_id)
                        }}
                      >
                        Verify Connection
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="v11-dashboard-card p-4">
            <h3 className="text-sm font-semibold text-slate-900">Add or update connection</h3>
            <p className="mt-1 text-xs text-slate-500">
              Save facility/vendor metadata and secret references only. Verification uses the shipped Sprint 3 backend contract.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="ehr-vendor" className="mb-1 block text-xs font-semibold text-slate-600">Vendor</label>
                <select
                  id="ehr-vendor"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={vendor}
                  onChange={(event) => setVendor(event.target.value as EhrVendor)}
                >
                  {Object.entries(EHR_VENDOR_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="ehr-auth-mode" className="mb-1 block text-xs font-semibold text-slate-600">Auth mode</label>
                <select
                  id="ehr-auth-mode"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={authMode}
                  onChange={(event) => setAuthMode(event.target.value as EhrAuthMode)}
                >
                  <option value="oauth2_client_credentials">OAuth 2.0 Client Credentials</option>
                  <option value="smart_on_fhir">SMART on FHIR</option>
                  <option value="api_key">API Key</option>
                </select>
              </div>

              <div>
                <label htmlFor="ehr-fhir-base-url" className="mb-1 block text-xs font-semibold text-slate-600">FHIR base URL</label>
                <input
                  id="ehr-fhir-base-url"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  placeholder="https://ehr.vendor.example/fhir/R4"
                  value={fhirBaseUrl}
                  onChange={(event) => setFhirBaseUrl(event.target.value)}
                />
              </div>

              {authMode !== 'api_key' && (
                <>
                  <div>
                    <label htmlFor="ehr-oauth-client-id" className="mb-1 block text-xs font-semibold text-slate-600">OAuth client ID</label>
                    <input
                      id="ehr-oauth-client-id"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                      value={oauthClientId}
                      onChange={(event) => setOauthClientId(event.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="ehr-oauth-secret-ref" className="mb-1 block text-xs font-semibold text-slate-600">OAuth client secret ref</label>
                    <input
                      id="ehr-oauth-secret-ref"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                      placeholder="EHR_VENDOR_CLIENT_SECRET"
                      value={oauthSecretRef}
                      onChange={(event) => setOauthSecretRef(event.target.value)}
                    />
                  </div>
                </>
              )}

              {authMode === 'api_key' && (
                <div>
                  <label htmlFor="ehr-api-key-ref" className="mb-1 block text-xs font-semibold text-slate-600">API key ref</label>
                  <input
                    id="ehr-api-key-ref"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                    placeholder="EHR_VENDOR_API_KEY"
                    value={apiKeyRef}
                    onChange={(event) => setApiKeyRef(event.target.value)}
                  />
                </div>
              )}

              <div>
                <label htmlFor="ehr-scopes" className="mb-1 block text-xs font-semibold text-slate-600">Scopes</label>
                <input
                  id="ehr-scopes"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  placeholder="system/*.read, patient/*.read"
                  value={scopesInput}
                  onChange={(event) => setScopesInput(event.target.value)}
                />
              </div>

              <label className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                <input
                  checked={baaConfirmed}
                  className="mt-0.5"
                  type="checkbox"
                  onChange={(event) => setBaaConfirmed(event.target.checked)}
                />
                <span>
                  I confirm a current BAA is in place for this facility and vendor before any verification step is run.
                </span>
              </label>

              <button
                className="btn-primary w-full text-sm"
                disabled={connectMutation.isPending || !canSubmitConnection}
                onClick={() => {
                  setEhrActionError(null)
                  setEhrActionSuccess(null)
                  connectMutation.mutate()
                }}
              >
                Save Connection
              </button>
            </div>
          </div>
        </div>

        {ehrActionError && <p className="mt-3 text-sm text-red-600">{ehrActionError}</p>}
        {ehrActionSuccess && <p className="mt-3 text-sm text-emerald-700">{ehrActionSuccess}</p>}
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
    </div>
  )
}
